import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { DebounceSettings } from 'lodash'; // eslint-disable-line lodash/import-scope -- Doesn't apply to importing types
import type { ActionCreator, AnyAction } from 'redux';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { useDebouncedCallback } from '../../../hooks';

const DEBOUNCE_TIMEOUT = 750;

//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

/**
 * Hook which will return a function which dispatches a given action
 * first, followed by dispatching a debounced version of the async fetchPackages.
 */
const useFetchPackages = (actionCreator: ActionCreator<AnyAction>, resetPage: boolean = false) => {
  const dispatch = useDispatch();

  //  Memoized debounce function, which re-fetches the packages.
  const dispatchFetchPackages = useDebouncedCallback(() => {
    dispatch(PackageInventoryStore.actions.fetchPackages());
  }, DEBOUNCE_TIMEOUT, DEBOUNCE_OPTIONS);

  //  This helper does three things, in synchronous order:
  //  - dispatch an action which will update the state immediately in response to user action
  //  - dispatch preFetchLoading to tell the UI that something is loading right away.
  //  - after the debounce timeout, the packages are fetched
  const fetchPackages = useCallback((...args) => {
    if (resetPage) {
      dispatch(PackageInventoryStore.actions.resetPackagesPage());
    }

    //  Dispatch the passed in action, which updates the Redux store immediately
    dispatch(actionCreator(...args));

    //  Update the UI right away so the user knows something is happening.
    dispatch(PackageInventoryStore.actions.preFetchLoading());

    dispatchFetchPackages();
  }, [actionCreator]);

  return fetchPackages;
};

export default useFetchPackages;
